import { useState } from 'react';
import { InputGroup } from '../common/InputGroup';
import { useTranslation } from 'react-i18next';
import { Button } from '../common/Button';
import { ProfileAvatarUpload } from './ProfileAvatarUpload';
import { useProfileStore } from '../../stores/profileStore';
import { toast } from 'react-toastify';
import type { Profile, ProfileAchievement } from '../../types/profile';
import FormLanguageSelector from '../common/FormLanguageSelector';
import { AchievementModal } from './AchievementModal';
import { Settings, PlusCircle, ChevronLeft } from 'lucide-react';
import { DropdownMultiTypeahead } from '../common/DropdownMultiTypeahead';
import { useServiceCategorySelector } from '../../hooks/useServiceCategorySelector';
import ProfileAvatar from '../common/ProfileAvatar';
import { createServiceGroupBy } from '../../utils/serviceUtils';

export interface EditProfileInfoProps {
  profile: Profile;
  onSave?: () => void;
  onNext?: () => void;
  onPrevious?: () => void;
  onSkip?: () => void;
}

const EditPublicProfile: React.FC<EditProfileInfoProps> = ({
  profile,
  onSave,
  onNext,
  onPrevious,
  onSkip,
}) => {
  const { t } = useTranslation(['auth', 'settings', 'common']);
  const [firstName, setFirstName] = useState(profile?.first_name || '');
  const [languages, setLanguages] = useState<string[]>(profile?.languages || []);
  const [description, setDescription] = useState(profile?.bio || '');
  const [imageUrl, setImageUrl] = useState(profile?.avatar_url || null);
  const [errors, setErrors] = useState<Record<string, string | undefined>>({});
  const [saving, setSaving] = useState(false);
  const { completeProfile, suggestNewService } = useProfileStore();

  const [isAchievementModalOpen, setIsAchievementModalOpen] = useState(false);
  const [selectedAchievement, setSelectedAchievement] = useState<ProfileAchievement | null>(null);

  const canAddAchievement = (profile?.achievements?.length ?? 0) < 5;

  const {
    selectedOptions: selectedServiceCategories,
    setSelectedOptions: setSelectedServiceCategories,
    filteredOptions: serviceCategoryOptions,
    setSearchInput: setServiceSearchInput,
  } = useServiceCategorySelector(profile?.services);

  const validate = () => {
    const newErrors: typeof errors = {};
    if (!languages.length) newErrors.languages = t('fieldRequired');
    if (!selectedServiceCategories.length) newErrors.services = t('fieldRequired');
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validate() || !profile?.id) return;

    setSaving(true);
    try {
      await completeProfile({
        userId: profile.id,
        firstName,
        lastName: profile.last_name || '',
        languages,
        bio: description,
        serviceIds: selectedServiceCategories.map(s => Number(s.id)),
      });

      toast.success(t('profileUpdateSuccess'));
      if (onNext) {
        onNext();
      } else if (onSave) {
        onSave();
      }
    } catch (err) {
      console.error(err);
      toast.error(t('profileUpdateError'));
    } finally {
      setSaving(false);
    }
  };

  const handleCreateNewService = async (query: string) => {
    try {
      const newService = await suggestNewService({
        label: query,
        description: '',
      });
      setSelectedServiceCategories([...selectedServiceCategories, newService]);
      toast.success(t('serviceAddedSuccess', { ns: 'settings', defaultValue: 'Service added!' }));
    } catch (err) {
      console.error('Error creating service:', err);
      toast.error(t('serviceAddError', { ns: 'settings', defaultValue: 'Could not add service.' }));
    }
  };

  const openAddAchievement = () => {
    setSelectedAchievement(null);
    setIsAchievementModalOpen(true);
  };

  return (
    <>
      <div>
        <div className="flex flex-col lg:flex-row gap-12 mt-8">
          {/* Avatar section */}
          <div className="flex justify-center lg:justify-start w-full lg:w-1/3">
            <ProfileAvatarUpload
              profileId={profile?.id || ''}
              imageUrl={imageUrl}
              size={192}
              design="settings"
              onChange={setImageUrl}
            />
          </div>

          {/* Form section */}
          <div className="w-full lg:w-2/3 grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
            <InputGroup
              label={t('firstName')}
              value={firstName}
              onChange={e => setFirstName(e.target.value)}
              placeholder={t('firstNamePlaceholder')}
              required
              error={!!errors.firstName}
              errorMessage={errors.firstName}
              disabled={true}
            />

            <InputGroup
              label={t('city')}
              value={profile?.city?.name || ''}
              placeholder={t('cityPlaceholder')}
              required
              disabled={true}
            />

            <FormLanguageSelector
              required
              value={languages}
              error={errors.languages}
              onChange={setLanguages}
            />

            <div className="col-span-1 md:col-span-2">
              <div className="flex flex-col gap-6">
                <InputGroup
                  label={t('servicesOffered')}
                  required
                  error={!!errors.services}
                  errorMessage={errors.services}
                >
                  <DropdownMultiTypeahead
                    selected={selectedServiceCategories}
                    onChange={items => {
                      setSelectedServiceCategories(items);
                      if (errors.services) setErrors(prev => ({ ...prev, services: undefined }));
                    }}
                    options={serviceCategoryOptions}
                    onSearchChange={setServiceSearchInput}
                    getOptionLabel={item => item.label}
                    getOptionValue={item => item.id}
                    placeholder={t('form_fields.servicesHint', { ns: 'common' })}
                    className="mt-4"
                    error={!!errors.services}
                    onCreate={handleCreateNewService}
                    groupBy={createServiceGroupBy(t)}
                    inputMode="search"
                  />
                </InputGroup>
              </div>
            </div>

            <div className="col-span-1 md:col-span-2">
              <p className="font-ambit text-[0.8125rem] font-normal leading-[1.375rem] text-[#3B5266] rounded-md border border-blue-100 bg-blue-50/70 px-3 py-2.5 mb-2">
                {t('professionalDescriptionNotice', { ns: 'settings' })}
              </p>
              <InputGroup
                label={t('professionalDescription', { ns: 'settings' })}
                value={description}
                onChange={e => setDescription(e.target.value)}
                as="textarea"
                placeholder={t('professionalDescriptionPlaceholder', { ns: 'settings' })}
                maxLength={1500}
              />
              <div className="text-right text-xs text-secondary pb-4">
                {1500 - description.length} {t('charactersRemaining', { ns: 'settings' })}
              </div>
            </div>

            {/* Achievements Section */}
            <div className="col-span-1 md:col-span-2">
              <p className="font-ambit text-base text-secondary mb-4">
                {t('tabs.achievements', { ns: 'settings', defaultValue: 'Mes réalisations' })}
              </p>

              {canAddAchievement && (
                <button
                  onClick={openAddAchievement}
                  className="flex flex-col items-center space-y-2 justify-center w-full mb-4 border-2 border-dashed border-gray-300 text-secondary font-medium rounded-xl py-4 hover:border-primary hover:text-primary transition-all bg-gray-50/50"
                >
                  <PlusCircle className="w-5 h-5 text-primary" />
                  <span className="block">
                    {t('achievements.achievementUploadHint', { ns: 'settings' })}
                  </span>
                </button>
              )}

              <div className="grid grid-cols-1 gap-4">
                {profile?.achievements?.map(achievement => (
                  <div
                    key={achievement.id}
                    className="bg-white p-3 rounded-xl border border-gray-100 shadow-sm flex items-center justify-between hover:shadow-md transition cursor-pointer"
                    onClick={() => {
                      setSelectedAchievement(achievement);
                      setIsAchievementModalOpen(true);
                    }}
                  >
                    <div className="flex items-center gap-4">
                      <ProfileAvatar
                        first_name={achievement.client_name}
                        avatar_url={achievement.project_image_url}
                      />
                      <div className="flex flex-col">
                        <span className="font-bold text-secondary">{achievement.project_name}</span>
                        <span className="text-sm text-secondary/70">{achievement.client_name}</span>
                      </div>
                    </div>
                    <Settings className="w-8 h-8 p-2 bg-gray-100 text-secondary rounded-full hover:bg-primary hover:text-white transition" />
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Save/Next/Skip/Back buttons */}
        <div className="flex flex-col gap-4 md:flex-row-reverse mt-8">
          <Button variant="primary" onClick={handleSave} className="w-full md:w-auto px-10 py-1">
            {saving
              ? t('common:form_actions.saving', { ns: 'common' })
              : onNext
                ? t('common:form_actions.next', { ns: 'common' })
                : t('improveProfileStep.save')}
          </Button>

          {onSkip && (
            <Button
              variant="transparent-with-border"
              onClick={onSkip}
              className="w-full md:w-auto px-10 py-1"
            >
              {t('common:form_actions.skip', { ns: 'common' })}
            </Button>
          )}

          {onPrevious && (
            <Button
              variant="simple-text"
              onClick={onPrevious}
              className="w-full md:w-auto px-10 py-1 flex items-center gap-2"
            >
              <ChevronLeft size={20} aria-hidden />
              {t('common:form_actions.previous', { ns: 'common' })}
            </Button>
          )}
        </div>
      </div>

      {isAchievementModalOpen && (
        <AchievementModal
          onClose={() => setIsAchievementModalOpen(false)}
          achievementToEdit={selectedAchievement}
        />
      )}
    </>
  );
};

export default EditPublicProfile;
